import {MaterialIcon} from "./types/MaterialIcon";
import {PanelForm} from "./types/panel-form";
import {DataItem, OptionData} from "../types/material-select";

type Constructor<T = {}> = new (...args: any[]) => T;
type ElementArg = HTMLDivElement | undefined | string;

export class MaterialModal extends PanelForm {
    private _modal: HTMLDivElement;
    private _icon: MaterialIcon | undefined;
    private _title: string | undefined;
    private _subtitle: string | undefined;
    private _footer: string;
    private _options: MaterialModalOptions | undefined;
    private _closeCallback: ((record: any) => void) | undefined;
    private _mInstance?: M.Modal;

    set onClose(callback: ((record: any) => void) | undefined) {
        this._closeCallback = callback;
    }

    constructor(modal: HTMLDivElement | undefined | null | string, options: MaterialModalOptions) {
        if (typeof modal === 'string') {
            modal = document.getElementById(modal) as HTMLDivElement;
        }
        if (!modal) {
            modal = MaterialModal.createModalElement(options);
            document.body.appendChild(modal);
        }
        super(modal);
        this._modal = modal;
        this.title = options.title || this.title || 'Error';
        this.content = options.content ?? this.content;
        this._footer = options.footer || `<a href="#!" class="modal-close waves-effect waves-green btn-flat">Close</a>`;
        this.initialize(modal, options);
        this.initSelectElements();
    }

    protected initialize(modal: HTMLDivElement | undefined | string, options: MaterialModalOptions) {
        this._options = options;
    }

    get icon(): string {
        const iconElement = this._modal.querySelector<MaterialIcon>('.modal-header h5 i');
        if (iconElement) {
            return iconElement.innerHTML || '';
        }
        return '';
    }

    set icon(value: string) {
        const iconElement = this._modal.querySelector('.modal-header h5 i');
        this._icon = new MaterialIcon(value);
        this._icon.innerHTML = value;
        if (iconElement) {
            iconElement.outerHTML = this._icon.outerHTML
        }
    }

    get title(): string {
        const titleElement = this._modal.querySelector('.modal-header h5 span');
        if (titleElement) {
            return titleElement.innerHTML || '';
        }
        return '';
    }

    set title(value: string) {
        const titleElement = this._modal.querySelector('.modal-header h5 span');
        this._title = value;
        if (titleElement) {
            titleElement.innerHTML = value;
        }
    }

    get subtitle(): string {
        const subtitleElement = this._modal.querySelector('.modal-header p');
        if (subtitleElement) {
            return subtitleElement.innerHTML || '';
        }
        return '';
    }

    set subtitle(value: string) {
        const subtitleElement = this._modal.querySelector('.modal-header p');
        this._subtitle = value;
        if (subtitleElement) {
            subtitleElement.innerHTML = value;
        }
    }

    public getModal(): HTMLDivElement {
        return this._modal;
    }

    public getFooter(): string {
        return this._footer;
    }

    public open(record?: any): Promise<void> {
        if (!record || Object.keys(record).length === 0) {
            this.clear();
        }

        return new Promise((resolve, reject) => {
            // Create a one-time listener for THIS specific open call
            const onOpenEndHandler = () => {
                // Wait for modal to be fully visible before initializing
                setTimeout(() => {
                    this.initMaterialize();
                    if (record) {
                        this.record = record;
                    }
                    if (this._options?.onOpen) {
                        this._options.onOpen();
                    }
                    resolve();
                }, 50);
            };

            if (!this._mInstance) {
                this._mInstance = M.Modal.init(this._modal, {
                    ...this._options,
                    onOpenEnd: onOpenEndHandler,
                    onCloseEnd: () => {
                        if(this._options?.onClose){
                            this._options.onClose();
                        }
                        resolve();
                    }
                });
            } else {
                // Instance exists - update the onOpenEnd callback for this open
                this._mInstance.options.onOpenEnd = onOpenEndHandler;
            }

            this._mInstance.open();
        });
    }

    public initMaterialize() {
        // Force re-initialization by resetting the gate
        this.resetSelectInitialization();
        this.initSelectElements();

        // Give the DOM a moment to settle after initialization
        setTimeout(() => {
            this._selectElements.forEach((value, key) => {
                value.reload();
            });
            M.updateTextFields();
        }, 100);
    }

    private resetSelectInitialization() {
        // Access the protected property through type casting
        (this as any)._selectsInitialized = false;
    }

    public close() {
        const instance = M.Modal.getInstance(this._modal);
        if (instance) {
            instance.close();
        }
        if (this._closeCallback) {
            const record = this.record || {};
            record.id = super.id;
            this._closeCallback(record);
        }
    }

    public getSelectValue(selectName: string): any {
        const selectInstance = this._selectElements.get(selectName);
        if (selectInstance) {
            return selectInstance.selected;
        }
        return null;
    }

    public getSelectData<T extends OptionData>(selectName: string): DataItem<T>[] | undefined {
        const selectInstance = this._selectElements.get(selectName);
        if (selectInstance) {
            return selectInstance.selectedItemsData;
        }
    }

    public getInputValue(selectName: string): any {
        const inputElement = this.getInput(selectName);
        if (inputElement) {
            return inputElement.value;
        }
    }

    public setFooter(footer: string) {
        const footerElement = this._modal.querySelector('.modal-footer');
        this._footer = footer;
        if (footerElement) {
            footerElement.innerHTML = footer;
        }
    }

    protected static createModalElement(options: MaterialModalOptions): HTMLDivElement {
        // Create a temporary container element to safely parse the HTML string.
        const tempContainer = document.createElement('div');
        const modalId = `modal-${Date.now()}`;
        tempContainer.id = modalId;

        // Use innerHTML to build the modal structure within the container.
        // This avoids the pitfalls of using outerHTML on an unattached element.
        // Also, wrap the footer content in a .modal-footer for correct styling.
        tempContainer.innerHTML = `
            <div class="modal modal-fixed-footer" id="${modalId}">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5>
                      <i class="material-icons">add</i>
                      <span>${options.title || 'Error'}</span>
                    </h5>
                  </div>
                  <div class="modal-body">
                    ${options.content || ''}
                  </div>
                </div>
                <div class="modal-footer">
                    ${options.footer || `<a href="#!" class="modal-close waves-effect waves-green btn-flat">Close</a>`}
                </div>
            </div>`.trim();

        // The actual modal is the first (and only) child of our container.
        const modalElement = tempContainer.firstChild as HTMLDivElement;

        // Return the correctly created modal element.
        // The constructor will handle appending it to the document body.
        return modalElement;
    }

    protected render(): string {
        return `<div class="modal modal-fixed-footer">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5>
                      ${this._icon?.outerHTML ?? 'add'}
                      <span>${this._title}</span>
                    </h5>
                    <p>${this._subtitle}</p>
                  </div>
                  <div class="modal-body">
                    ${this.content}
                  </div>
                </div>
                <div class="modal-footer">
                    ${this._footer}
                </div>
              </div>`
    }

    connectedCallback() {
        this._icon = new MaterialIcon('error');
        this._title = 'Error';
        this._subtitle = 'An error occurred';
        this.content = '';
        this._footer = `
            <a href="#!" class="modal-close waves-effect waves-green btn-flat">Close</a>
        `;
    }

    destroy() {
        this._mInstance?.destroy();
    }
}

export interface MaterialModalOptions {
    title?: string;
    content?: string;
    footer?: string;
    onClose?: () => void;
    dismissible?: boolean;
    onOpen?: () => void;
    onSubmit?: () => void;
    onCancel?: () => void;
    submitText?: string;
    cancelText?: string;
    submitIcon?: string;
    dontInitSelects?: boolean;
}
